<template>
  <div ref="rootElement" class="fast-download">
    <button
      @click="startDownload"
      :disabled="busy"
      class="btn btn-outline-primary btn-sm btn-block d-flex align-items-center justify-content-center fast-dl-start">
      <slot>{{ startButtonLabel }}</slot>
    </button>
    <div v-if="showProgress" class="fast-download-progress">
      <progress max="100" :value="progressState.percent" class="fast-download-progress-bar"></progress>
    </div>
    <p v-if="statusMessage" class="status">{{ statusMessage }}</p>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, onMounted } from 'vue'

const props = defineProps({
  videoId: { type: [Number, String], required: true },
  suggestedName: { type: String, default: '' },
  chunkMb: { type: Number, default: 32 },
  initialConcurrency: { type: Number, default: 12 },
  signedUrl: { type: String, default: '' },
  signedUrlEndpoint: { type: String, default: '' },
  logEndpoint: { type: String, default: '' },
  startLabel: { type: String, default: '' },
  fallbackUrl: { type: String, default: '' },
  manualTrigger: { type: Boolean, default: false },
  maxConcurrency: { type: Number, default: 0 },
  // 順序書き込み（単一ライタ）を有効化するか（既定: 無効 = 高速寄り）
  orderedWrite: { type: Boolean, default: false },
  knownTotalBytes: { type: Number, default: 0 },
  rangeSupportedHint: { type: String, default: '' },
  headSkipThresholdBytes: { type: Number, default: 30 * 1024 * 1024 * 1024 },
})

const startButtonLabel = computed(() => (props.startLabel || '').trim() || 'ダウンロード開始')
const isFsAccess = 'showSaveFilePicker' in window
const busy = ref(false)
const statusMessage = ref('')
const downloader = ref(null)
const progressState = ref({ percent: 0, downloaded: 0, total: 0, seconds: 0 })
const progressLabel = ref('')
const showProgress = computed(() => isFsAccess && busy.value && progressState.value.total > 0)
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
const csrfHeaders = csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}

const HEAD_SKIP_THRESHOLD_DEFAULT = 30 * 1024 * 1024 * 1024
const FAST_DOWNLOAD_THRESHOLD_BYTES = 50 * 1024 * 1024 * 1024
const remoteMetadata = ref({ size: 0, acceptRanges: '', skipHead: false })

const normalizeAcceptRanges = (value) => {
  if (typeof value !== 'string') return ''
  return value.trim().toLowerCase()
}

const parseSizeValue = (value) => {
  if (value === undefined || value === null) return 0
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) return 0
  return numeric
}

const resolveHeadSkipThreshold = () => {
  const raw = Number(props.headSkipThresholdBytes)
  return Number.isFinite(raw) && raw > 0 ? raw : HEAD_SKIP_THRESHOLD_DEFAULT
}

const rootElement = ref(null)
let manualStartTarget = null
const manualTrigger = computed(() => Boolean(props.manualTrigger))

const setStatus = (...args) => {
  const message = args
    .filter((value) => value !== undefined && value !== null && String(value).trim() !== '')
    .map((value) => (typeof value === 'string' ? value : String(value)))
    .join(' ')
  statusMessage.value = message
  if (message) {
    console.debug('[FastDownload]', message)
  }
}

const handleManualStart = (event) => {
  const prepLabel = event?.detail?.label
  if (typeof prepLabel === 'string' && prepLabel.trim()) {
    setStatus(prepLabel)
  }
  startDownload()
}

onMounted(() => {
  if (!manualTrigger.value) {
    return
  }
  manualStartTarget = rootElement.value || null
  if (manualStartTarget) {
    manualStartTarget.addEventListener('fast-dl:start', handleManualStart)
  }
})

const isRangeUnsupportedError = (error) => {
  const rawMessage = error?.message ?? (typeof error === 'string' ? error : '')
  const message = typeof rawMessage === 'string' ? rawMessage : ''
  if (message.includes('Range未対応')) {
    return true
  }
  const code = error?.code || ''
  const name = error?.name || ''
  return code === 'RangeUnsupported' || name === 'RangeUnsupported'
}

const MAX_RETRIES = 3

const sanitizeName = (value) => (typeof value === 'string' ? value.trim() : '')

const extractExtension = (value) => {
  if (typeof value !== 'string' || !value) return ''
  const sanitized = value.split(/[?#]/)[0]
  const lastSegment = sanitized.split('/').pop() || ''
  const match = lastSegment.match(/\.([A-Za-z0-9]{1,8})$/)
  return match ? match[0] : ''
}

const extractUrlFilename = (url) => {
  if (typeof url !== 'string' || !url) return ''
  try {
    const parsed = new URL(url)
    return parsed.pathname.split('/').pop() || ''
  } catch (_) {
    const sanitized = url.split(/[?#]/)[0]
    return sanitized.split('/').pop() || ''
  }
}

const appendExtension = (name, ext) => {
  if (!name) return ''
  if (!ext) return name
  return name.toLowerCase().endsWith(ext.toLowerCase()) ? name : `${name}${ext}`
}

const ensureDownloadName = (preferred, fallback, url) => {
  const cleanPreferred = sanitizeName(preferred)
  const cleanFallback = sanitizeName(fallback)
  const preferredExt = extractExtension(cleanPreferred)
  if (preferredExt) return cleanPreferred

  const fallbackExt = extractExtension(cleanFallback)
  if (fallbackExt) {
    if (cleanPreferred) {
      return appendExtension(cleanPreferred, fallbackExt)
    }
    return cleanFallback
  }

  const urlExt = extractExtension(extractUrlFilename(url))
  if (urlExt) {
    if (cleanPreferred) return appendExtension(cleanPreferred, urlExt)
    if (cleanFallback) return appendExtension(cleanFallback, urlExt)
    return appendExtension('download', urlExt)
  }

  return cleanPreferred || cleanFallback || 'download.bin'
}

const formatBytes = (n) => {
  if (!Number.isFinite(n) || n < 0) return '---'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = n
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i += 1
  }
  return `${value.toFixed(1)} ${units[i]}`
}

class DownloadCancelled extends Error {
  constructor(message = 'cancelled') {
    super(message)
    this.name = 'DownloadCancelled'
  }
}

async function requestSignedUrl() {
  if (!props.signedUrlEndpoint) throw new Error('署名URLエンドポイントが未設定です')
  const endpoint = `${props.signedUrlEndpoint}?video=${encodeURIComponent(props.videoId)}`
  const r = await fetch(endpoint, { credentials: 'include' })
  if (r.status === 404) throw new Error('対象のファイルが見つかりません')
  if (!r.ok) {
    const body = await r.json().catch(() => ({}))
    throw new Error(body?.message || '署名URL取得に失敗')
  }
  return r.json()
}

async function headInfo(url) {
  const r = await fetch(url, { method: 'HEAD', mode: 'cors' })
  if (!r.ok) throw new Error('HEAD失敗: ' + r.status)
  const cd = r.headers.get('content-disposition') || ''
  return {
    size: Number(r.headers.get('content-length')),
    acceptRanges: (r.headers.get('accept-ranges') || '').toLowerCase(),
    name: cd.match(/filename="(.+?)"/)?.[1],
  }
}

//

async function recordDownloadLog() {
  if (!props.logEndpoint) return
  try {
    const response = await fetch(props.logEndpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: csrfHeaders,
      keepalive: true,
    })
    if (!response.ok) {
      throw new Error(`status ${response.status}`)
    }
  } catch (error) {
    console.warn('[FastDownload] Failed to record download log', error)
  }
}

const normalizeMetadata = (metadata) => {
  const meta = metadata && typeof metadata === 'object' ? metadata : {}
  return {
    size: parseSizeValue(meta.size ?? meta.total ?? meta.length),
    acceptRanges: normalizeAcceptRanges(meta.acceptRanges ?? meta.accept_ranges),
    name: typeof meta.name === 'string' ? meta.name : '',
    skipHead: Boolean(meta.skipHead),
  }
}

  class FastDownloader {
  constructor({ url, chunkSize, concurrency, suggestedName, logger, onProgress, maxConcurrency, orderedWrite, metadata, maxAheadBytes }) {
    this.url = url
    this.chunkSize = chunkSize
    this.concurrency = Math.max(1, Math.floor(concurrency) || 1)
    const requestedMax = Number.isFinite(maxConcurrency) ? Math.floor(maxConcurrency) : 0
    // Allow a bit more headroom by default to saturate fast links/CDNs
    const fallbackMax = Math.max(this.concurrency, Math.min(this.concurrency * 3, this.concurrency + 8))
    const computedMax = requestedMax > 0 ? Math.max(this.concurrency, requestedMax) : fallbackMax
    this.maxConcurrency = Math.min(32, Math.max(this.concurrency, computedMax))
    this._targetConcurrency = this.concurrency
    this._adaptiveEnabled = this.maxConcurrency > this.concurrency
    this._adjustIntervalMs = 1000
    this._lastAdjustAt = 0
    this._activeWorkers = new Set()
    this._pendingError = null
    this.suggestedName = suggestedName
    this._log = logger
    this._onProgress = typeof onProgress === 'function' ? onProgress : () => {}
    this._now = typeof performance !== 'undefined' && typeof performance.now === 'function' ? () => performance.now() : () => Date.now()
    this._controller = new AbortController()
    this._queue = []
    this._sink = null
    this._cancelledByUser = false
    // Ordered write management (configurable)
    this._orderedWrite = Boolean(orderedWrite)
    this._metadata = normalizeMetadata(metadata)
    this._chunks = [] // [{ start, end, buffers: Uint8Array[] | null, ready: boolean }]
    this._chunkResolvers = [] // [{ promise, resolve }]
    this._writerPromise = null
    this.totalSize = this._metadata.size || 0
    this.downloadedBytes = 0
    this.writtenBytes = 0
    this.startedAt = 0
    // Progress throttling (minimize main-thread work)
    this._progressIntervalMs = 10000
    this._lastProgressAt = 0
    // Throughput tracking for adaptive concurrency
    this._throughputWindowStartAt = 0
    this._throughputBytesAtStart = 0
    this._bpsEwma = 0
    this._lastBps = 0
    this._lastDecreaseAt = 0
    this._rangeVerified = false
    // Per-chunk timeout (to cut long-tail stalls)
    this._chunkTimeoutMs = 15000
    // Warm-up period for faster ramp-up
    this._warmupUntil = this._now() + 5000
    // Backpressure (ordered write): cap how far downloads may run ahead of the writer
    const requestedAhead = Number.isFinite(maxAheadBytes) && maxAheadBytes > 0 ? maxAheadBytes : 1024 * 1024 * 1024
    this._maxAheadChunks = Math.max(1, Math.floor(requestedAhead / this.chunkSize))
    this._writerIndex = 0
    this._windowWaiters = []
    // Per-write timeout (surface a hung disk write instead of stalling silently)
    this._writeTimeoutMs = 120000
    // Retries scheduled but not yet re-queued (backoff timers in flight).
    // The main loop must not treat the download as finished while one exists,
    // or a retried chunk is silently dropped and the file closes with a hole.
    this._pendingRetries = 0
  }

  cancel() {
    this._cancelledByUser = true
    this._controller.abort()
    this._queue.length = 0
    this._releaseChunkWaiters()
    this._notifyWindowWaiters()
    if (this._sink) {
      this._sink.abort().catch(() => {})
    }
  }

  // 失敗確定・キャンセル時に、_writerLoop が永久に待たないよう未解決のチャンク待ちを全て解放する
  _releaseChunkWaiters() {
    for (const d of this._chunkResolvers) {
      if (d && typeof d.resolve === 'function') {
        try { d.resolve() } catch (_) {}
      }
    }
  }

  _notifyWindowWaiters() {
    const waiters = this._windowWaiters
    this._windowWaiters = []
    for (const w of waiters) {
      try { w() } catch (_) {}
    }
  }

  // Ordered write: take the next job only when it is within the write-ahead window.
  // Without this, downloads run arbitrarily far ahead of the writer and buffered
  // chunks accumulate in memory without bound. The window check and the shift
  // must happen in the same synchronous block: an await between them lets
  // multiple workers pass the same check and overshoot the window.
  async _takeNextJob() {
    if (!this._orderedWrite) {
      return this._queue.shift()
    }
    while (!this._pendingError && !this._cancelledByUser) {
      const head = this._queue[0]
      if (!head) return undefined
      if (head.index < this._writerIndex + this._maxAheadChunks) {
        return this._queue.shift()
      }
      await new Promise((resolve) => { this._windowWaiters.push(resolve) })
    }
    return undefined
  }

  async start() {
    let size = this._metadata.size
    let acceptRanges = this._metadata.acceptRanges
    let headName = this._metadata.name || ''

    if (!this._metadata.skipHead || !Number.isFinite(size) || size <= 0 || !acceptRanges.includes('bytes')) {
      const info = await headInfo(this.url)
      size = parseSizeValue(info.size)
      acceptRanges = normalizeAcceptRanges(info.acceptRanges)
      headName = info.name || headName
    } else if (typeof this._log === 'function') {
      this._log('INFO: HEAD request skipped (using provided metadata)')
    }

    if (!Number.isFinite(size) || size <= 0) throw new Error('ファイルサイズが取得できません')
    if (!acceptRanges.includes('bytes')) throw new Error('Range未対応')

    size = Math.floor(size)

    const suggestedName = ensureDownloadName(this.suggestedName, headName, this.url)
    const handle = await window.showSaveFilePicker({ suggestedName }).catch((err) => {
      if (err?.name === 'AbortError') throw new DownloadCancelled('ユーザーが保存をキャンセルしました')
      throw err
    })

    this._sink = await handle.createWritable()
    if (typeof this._log === 'function') {
      this._log(`INFO: 合計 ${formatBytes(size)} / リクエストチャンク ${formatBytes(this.chunkSize)} / 並列 ${this._targetConcurrency} (最大 ${this.maxConcurrency})`)
    }

    this.totalSize = size
    this.downloadedBytes = 0
    this.writtenBytes = 0
    this.startedAt = this._now()
    this._rangeVerified = false
    this._notifyProgress()

    // Pre-size the file to avoid repeated growth during random writes
    try {
      await this._sink.truncate(size)
    } catch (e) {
      // Ignore if not supported; writing will still proceed
      console.debug('[FastDownload] truncate not supported or failed', e)
    }

    // Prepare queue and descriptors for ordered write
    this._chunks.length = 0
    this._chunkResolvers.length = 0
    let idx = 0
    for (let start = 0; start < size; start += this.chunkSize) {
      const end = Math.min(start + this.chunkSize, size)
      this._chunks.push({ start, end, buffers: null, ready: false })
      let resolve
      const promise = new Promise((r) => { resolve = r })
      this._chunkResolvers.push({ promise, resolve })
      this._queue.push({ index: idx, start, end, tries: 0 })
      idx += 1
    }
    if (this._queue.length === 0) {
      await this._sink.close()
      return
    }

    this._ensureWorkers(Math.min(this._targetConcurrency, this._queue.length))
    // Start single writer to write chunks in order (when enabled)
    if (this._orderedWrite) {
      // Fail fast: a writer error must stop downloads immediately, not after
      // every remaining chunk has been fetched into memory.
      this._writerPromise = this._writerLoop().catch((err) => {
        if (!this._pendingError) {
          this._pendingError = err
        }
        try { this._controller.abort() } catch (_) {}
        this._releaseChunkWaiters()
        this._notifyWindowWaiters()
      })
    }

    try {
      while (!this._pendingError && !this._cancelledByUser && (this._queue.length > 0 || this._activeWorkers.size > 0 || this._pendingRetries > 0)) {
        if (this._activeWorkers.size === 0) {
          this._ensureWorkers(Math.min(this._targetConcurrency, Math.max(1, this._queue.length)))
          if (this._activeWorkers.size === 0) {
            if (this._pendingRetries > 0) {
              // Backoff timer in flight: wait for it to re-queue the job
              await new Promise((resolve) => setTimeout(resolve, 50))
              continue
            }
            break
          }
        }
        await Promise.race(this._activeWorkers)
      }

      await Promise.allSettled(this._activeWorkers)
      // Ensure writer has completed writing sequentially
      if (this._writerPromise) {
        await this._writerPromise
      }

      if (this._pendingError) throw this._pendingError
      if (this._cancelledByUser) throw new DownloadCancelled('ユーザーがダウンロードを中断しました')

      await this._sink.close()
    } catch (err) {
      if (this._sink) {
        try { await this._sink.abort() } catch (_) {}
      }
      if (err instanceof DownloadCancelled) throw err
      if (this._cancelledByUser) throw new DownloadCancelled('ユーザーがダウンロードを中断しました')
      throw err
    } finally {
      this._sink = null
      this._queue.length = 0
      this._activeWorkers.clear()
      this._pendingError = null
      this._rangeVerified = false
      this._targetConcurrency = this.concurrency
      this._chunks.length = 0
      this._chunkResolvers.length = 0
      this._writerPromise = null
      this._writerIndex = 0
      this._pendingRetries = 0
      this._notifyWindowWaiters()
    }
  }

  _notifyProgress() {
    if (!Number.isFinite(this.totalSize) || this.totalSize <= 0) {
      return
    }
    const now = this._now()
    if (this.downloadedBytes < this.totalSize) {
      if (this._lastProgressAt && now - this._lastProgressAt < this._progressIntervalMs) {
        return
      }
    }
    const elapsedMs = this.startedAt ? Math.max(0, now - this.startedAt) : 0
    try {
      this._onProgress(this.downloadedBytes, this.totalSize, elapsedMs / 1000, this.writtenBytes)
    } catch (error) {
      console.warn('[FastDownload] 進捗更新に失敗しました', error)
    }
    this._lastProgressAt = now
    if (this._adaptiveEnabled) {
      this._adjustConcurrency()
    }
  }

  _ensureWorkers(target) {
    if (this._cancelledByUser || this._pendingError) {
      return
    }
    const limit = Math.min(Math.max(1, target), this.maxConcurrency)
    while (this._activeWorkers.size < limit && this._queue.length > 0) {
      this._spawnWorker()
    }
  }

  _spawnWorker() {
    if (this._cancelledByUser || this._pendingError || this._queue.length === 0) {
      return
    }
    const workerPromise = this._worker()
      .catch((err) => {
        if (!this._pendingError) {
          this._pendingError = err
          try {
            this._controller.abort()
          } catch (_) {}
        }
        this._releaseChunkWaiters()
        this._notifyWindowWaiters()
      })
      .finally(() => {
        this._activeWorkers.delete(workerPromise)
        if (!this._pendingError && !this._cancelledByUser && this._queue.length > 0) {
          this._ensureWorkers(this._targetConcurrency)
        }
      })
    this._activeWorkers.add(workerPromise)
  }

  _adjustConcurrency() {
    if (!this._adaptiveEnabled || this._pendingError || this._cancelledByUser) {
      return
    }
    const now = this._now()
    const intervalOk = !this._lastAdjustAt || (now - this._lastAdjustAt >= this._adjustIntervalMs)
    // Update throughput window (about every call; uses a minimal 800ms window for stability)
    if (!this._throughputWindowStartAt) {
      this._throughputWindowStartAt = now
      this._throughputBytesAtStart = this.downloadedBytes
    } else {
      const winMs = now - this._throughputWindowStartAt
      if (winMs >= 800) {
        const deltaBytes = Math.max(0, this.downloadedBytes - this._throughputBytesAtStart)
        const bps = deltaBytes > 0 ? (deltaBytes * 1000) / winMs : 0
        this._bpsEwma = this._bpsEwma ? (this._bpsEwma * 0.7 + bps * 0.3) : bps
        this._throughputWindowStartAt = now
        this._throughputBytesAtStart = this.downloadedBytes
      }
    }
    if (!intervalOk) {
      return
    }
    if (this._activeWorkers.size < this._targetConcurrency) {
      return
    }
    if (this._queue.length <= this._targetConcurrency) {
      return
    }
    // If throughput dropped notably vs previous measurement and we have room, decrease first
    const canDecrease = this._targetConcurrency > this.concurrency && (now - this._lastDecreaseAt >= this._adjustIntervalMs)
    if (canDecrease && this._lastBps && this._bpsEwma && this._bpsEwma < this._lastBps * 0.9) {
      this._targetConcurrency = Math.max(this.concurrency, this._targetConcurrency - 1)
      this._lastDecreaseAt = now
      this._lastAdjustAt = now
      if (typeof this._log === 'function') {
        this._log(`INFO: 並列数を ${this._targetConcurrency} へ減少 (スループット低下)`) 
      }
      this._lastBps = this._bpsEwma || this._lastBps
      return
    }
    this._lastBps = this._bpsEwma || this._lastBps
    if (this._targetConcurrency >= this.maxConcurrency) {
      return
    }
    const step = (now < this._warmupUntil) ? 2 : 1
    this._targetConcurrency = Math.min(this.maxConcurrency, this._targetConcurrency + step)
    this._lastAdjustAt = now
    if (typeof this._log === 'function') {
      this._log(`INFO: 並列数を ${this._targetConcurrency} へ増加 (残りチャンク ${this._queue.length})`)
    }
    this._ensureWorkers(this._targetConcurrency)
  }

  async _worker() {
    while (!this._cancelledByUser && !this._pendingError) {
      const job = await this._takeNextJob()
      if (this._cancelledByUser || this._pendingError) return
      if (!job) return
      try {
        await this._downloadChunk(job)
      } catch (err) {
        if (this._cancelledByUser || err?.name === 'AbortError') {
          return
        }
        if (job.tries < MAX_RETRIES) {
          job.tries += 1
          if (typeof this._log === 'function') {
            this._log(`WARN: チャンク ${job.start}-${job.end} をリトライ (${job.tries}/${MAX_RETRIES})`)
          }
          // Exponential backoff with jitter to relieve congestion
          const base = 300
          const delay = Math.min(4000, base * Math.pow(2, job.tries - 1)) + Math.floor(Math.random() * 200)
          this._pendingRetries += 1
          setTimeout(() => {
            this._pendingRetries -= 1
            if (this._pendingError || this._cancelledByUser) return
            if (this._orderedWrite) {
              this._queue.unshift(job)
            } else {
              this._queue.push(job)
            }
            this._notifyWindowWaiters()
            this._ensureWorkers(this._targetConcurrency)
          }, delay)
        } else {
          if (!this._pendingError) {
            this._pendingError = err
          }
          this._queue.length = 0
          try {
            this._controller.abort()
          } catch (_) {}
          throw err
        }
      }
    }
  }

  

  async _waitForChunk(index) {
    if (this._chunks[index]?.ready) return
    const d = this._chunkResolvers[index]
    if (d?.promise) {
      await d.promise
    }
  }

  async _writerLoop() {
    if (!this._sink) return
    for (let i = 0; i < this._chunks.length; i++) {
      if (this._cancelledByUser || this._pendingError) return
      await this._waitForChunk(i)
      if (this._cancelledByUser || this._pendingError) return
      const chunk = this._chunks[i]
      if (!chunk || !chunk.buffers) {
        throw new Error('書き込み対象のチャンクが不正です')
      }
      // Write raw TypedArrays instead of a Blob: reading back a large Blob can
      // fail with NotReadableError (Chrome pages big blobs to temp storage).
      let offset = chunk.start
      for (const part of this._coalesceBuffers(chunk.buffers)) {
        await this._writeWithTimeout(part, offset)
        offset += part.byteLength
        this.writtenBytes += part.byteLength
      }
      // release memory
      chunk.buffers = null
      this._writerIndex = i + 1
      this._notifyWindowWaiters()
    }
  }

  // Merge small network-read buffers into at most ~8MiB TypedArrays to keep
  // the number of write() calls low without ever materializing a huge Blob.
  // 8MiB keeps each contiguous allocation small enough to survive heap
  // fragmentation; if even that allocation fails, fall back to passing the
  // original buffers through unmerged so the writer never needs a new
  // allocation at all.
  _coalesceBuffers(buffers) {
    const LIMIT = 8 * 1024 * 1024
    const parts = []
    let batch = []
    let batchSize = 0
    const flush = () => {
      if (batchSize === 0) return
      if (batch.length === 1) {
        parts.push(batch[0])
      } else {
        try {
          const merged = new Uint8Array(batchSize)
          let pos = 0
          for (const b of batch) {
            merged.set(b, pos)
            pos += b.length
          }
          parts.push(merged)
        } catch (_) {
          for (const b of batch) {
            parts.push(b)
          }
        }
      }
      batch = []
      batchSize = 0
    }
    for (const b of buffers) {
      batch.push(b)
      batchSize += b.length
      if (batchSize >= LIMIT) flush()
    }
    flush()
    return parts
  }

  async _writeWithTimeout(data, position) {
    let timer
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        const err = new Error('ファイル書き込みがタイムアウトしました')
        err.name = 'WriteTimeout'
        reject(err)
      }, this._writeTimeoutMs)
    })
    try {
      await Promise.race([this._sink.write({ type: 'write', position, data }), timeout])
    } finally {
      clearTimeout(timer)
    }
  }

  async _downloadChunk(job) {
    const expected = job.end - job.start
    // Create a per-request controller tied to the master controller and a timeout
    const localController = new AbortController()
    const onParentAbort = () => {
      try { localController.abort(this._controller.signal.reason || new DOMException('Aborted', 'AbortError')) } catch (_) {}
    }
    this._controller.signal.addEventListener('abort', onParentAbort)
    const timeoutId = setTimeout(() => {
      try { localController.abort(new Error('ChunkTimeout')) } catch (_) {}
    }, this._chunkTimeoutMs)
    let res
    try {
      res = await fetch(this.url, {
        headers: { Range: `bytes=${job.start}-${job.end - 1}` },
        mode: 'cors',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
        signal: localController.signal,
      })
    } catch (e) {
      clearTimeout(timeoutId)
      this._controller.signal.removeEventListener('abort', onParentAbort)
      // Distinguish timeout from user/global abort to enable retry
      if (localController.signal.aborted && !this._controller.signal.aborted) {
        const err = new Error('チャンクタイムアウト')
        err.name = 'ChunkTimeout'
        throw err
      }
      throw e
    }
    clearTimeout(timeoutId)
    this._controller.signal.removeEventListener('abort', onParentAbort)
    if (res.status !== 206) throw new Error(`Unexpected status ${res.status}`)
    if (!res.body) throw new Error('レスポンスにボディがありません')

    if (!this._rangeVerified) {
      const contentRange = res.headers.get('content-range') || ''
      const match = contentRange.match(/\/(\d+)$/)
      if (match) {
        const totalFromHeader = Number(match[1])
        if (Number.isFinite(totalFromHeader) && totalFromHeader > 0) {
          if (this.totalSize && totalFromHeader !== this.totalSize) {
            throw new Error('ファイルサイズ情報が一致しません')
          }
          this.totalSize = totalFromHeader
          this._rangeVerified = true
        }
      }
    }

    const reader = res.body.getReader()
    // Guard against a hung stream: fetch()'s timeout only covers header
    // reception, so without this a stalled reader.read() would wait forever
    // with no error and no progress.
    const readWithStallTimeout = () => new Promise((resolve, reject) => {
      let settled = false
      const stallTimer = setTimeout(() => {
        if (settled) return
        settled = true
        reader.cancel(new Error('ChunkStall')).catch(() => {})
        const err = new Error('チャンク読み込みが停滞しました')
        err.name = 'ChunkStall'
        reject(err)
      }, this._chunkTimeoutMs)
      reader.read().then((result) => {
        if (settled) return
        settled = true
        clearTimeout(stallTimer)
        resolve(result)
      }).catch((err) => {
        if (settled) return
        settled = true
        clearTimeout(stallTimer)
        reject(err)
      })
    })
    let received = 0
    if (this._orderedWrite) {
      // Collect full chunk; writer writes sequentially
      let buffers = []
      while (true) {
        const { done, value } = await readWithStallTimeout()
        if (done) break
        if (value && value.length) {
          received += value.length
          buffers.push(value)
          this.downloadedBytes = Math.min(this.totalSize, this.downloadedBytes + value.length)
          this._notifyProgress()
        }
      }
      if (received !== expected) {
        throw new Error(`チャンク長が不一致 (expected ${expected}, got ${received})`)
      }
      const idx = job.index
      const entry = this._chunks[idx]
      if (!entry || entry.start !== job.start || entry.end !== job.end) {
        throw new Error('チャンクメタデータの不整合')
      }
      entry.buffers = buffers
      entry.ready = true
      const d = this._chunkResolvers[idx]
      if (d?.resolve) {
        try { d.resolve() } catch (_) {}
      }
      return
    }
    // Unordered write with batching
    // Use a larger flush threshold to reduce write syscall overhead
    const FLUSH_THRESHOLD = Math.min(this.chunkSize, 4 * 1024 * 1024) // up to 4MiB or chunk size
    let buffers = []
    let bufferedBytes = 0
    let flushedBytes = 0
    while (true) {
      const { done, value } = await readWithStallTimeout()
      if (done) break
      if (value && value.length) {
        received += value.length
        if (!this._sink) throw new Error('書き込み先が初期化されていません')
        buffers.push(value)
        bufferedBytes += value.length
        if (bufferedBytes >= FLUSH_THRESHOLD) {
          for (const part of this._coalesceBuffers(buffers)) {
            await this._sink.write({ type: 'write', position: job.start + flushedBytes, data: part })
            flushedBytes += part.byteLength
            this.writtenBytes += part.byteLength
          }
          buffers = []
          bufferedBytes = 0
        }
        this.downloadedBytes = Math.min(this.totalSize, this.downloadedBytes + value.length)
        this._notifyProgress()
      }
    }
    if (bufferedBytes > 0) {
      for (const part of this._coalesceBuffers(buffers)) {
        await this._sink.write({ type: 'write', position: job.start + flushedBytes, data: part })
        flushedBytes += part.byteLength
        this.writtenBytes += part.byteLength
      }
    }
    if (received !== expected) {
      throw new Error(`チャンク長が不一致 (expected ${expected}, got ${received})`)
    }
  }
}

function triggerBrowserDownload(url, filename) {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

async function resolveDownloadSource() {
  if (props.signedUrl) {
    return {
      url: props.signedUrl,
      name: props.suggestedName || '',
      size: props.knownTotalBytes,
      acceptRanges: props.rangeSupportedHint,
    }
  }
  return requestSignedUrl()
}

async function startDownload() {
  if (busy.value) return
  statusMessage.value = ''
  progressState.value = { percent: 0, downloaded: 0, total: 0, seconds: 0 }
  progressLabel.value = ''

  let resolvedUrl = ''
  let filename = ''

  try {
    busy.value = true
    setStatus('ダウンロードを準備しています...')
    const source = await resolveDownloadSource()
    resolvedUrl = source.url
    const providedName = source.name
    filename = ensureDownloadName(props.suggestedName, providedName, resolvedUrl)

    const sizeFromSource = parseSizeValue(source?.size ?? source?.total ?? source?.length)
    const fallbackSize = parseSizeValue(props.knownTotalBytes)
    const resolvedSize = sizeFromSource || fallbackSize
    const hintedRanges = normalizeAcceptRanges(source?.acceptRanges ?? source?.accept_ranges ?? props.rangeSupportedHint)
    const headThreshold = resolveHeadSkipThreshold()
    const skipHead = resolvedSize > 0 && hintedRanges.includes('bytes') && headThreshold > 0 && resolvedSize >= headThreshold
    remoteMetadata.value = { size: resolvedSize, acceptRanges: hintedRanges, skipHead }
    if (resolvedSize > 0) {
      progressState.value = { percent: 0, downloaded: 0, total: resolvedSize, seconds: 0 }
    }
    if (skipHead) {
      console.debug('[FastDownload]', `Skipping HEAD request (size=${resolvedSize})`)
    }

    const shouldUseFastDownload = isFsAccess && resolvedSize >= FAST_DOWNLOAD_THRESHOLD_BYTES

    if (!shouldUseFastDownload) {
      if (!extractExtension(filename)) {
        try {
          const { name: headName } = await headInfo(resolvedUrl)
          filename = ensureDownloadName(filename, headName, resolvedUrl)
        } catch (error) {
          setStatus('⚠ ファイル名の取得に失敗したため、既定名で保存します')
        }
      }
      await recordDownloadLog()
      statusMessage.value = ''
      triggerBrowserDownload(resolvedUrl, filename)
      return
    }

    setStatus('ダウンロード中です...')

    const chunkSize = Math.max(1, props.chunkMb) * 1024 * 1024
    const instance = new FastDownloader({
      url: resolvedUrl,
      chunkSize,
      concurrency: props.initialConcurrency,
      maxConcurrency: props.maxConcurrency,
      suggestedName: filename,
      orderedWrite: Boolean(props.orderedWrite),
      metadata: { size: resolvedSize, acceptRanges: hintedRanges, skipHead, name: providedName },
      logger: (...info) => console.debug('[FastDownload]', ...info),
      onProgress: (done, total, elapsedSeconds, writtenBytes) => {
        if (!Number.isFinite(total) || total <= 0) {
          return
        }
        const clampedDone = Math.min(done, total)
        const percentRaw = Math.min(100, (clampedDone / total) * 100)
        const percentValue = Number(percentRaw.toFixed(2))
        const percentText = percentValue % 1 === 0 ? percentValue.toFixed(0) : percentValue.toFixed(1)
        const seconds = Math.max(0, elapsedSeconds || 0)
        const roundedSeconds = Math.round(seconds)
        let elapsedText
        if (roundedSeconds >= 60) {
          const minutes = Math.floor(roundedSeconds / 60)
          const remainingSeconds = roundedSeconds % 60
          elapsedText = remainingSeconds === 0
            ? `${minutes}分`
            : `${minutes}分${remainingSeconds.toString().padStart(2, '0')}秒`
        } else {
          elapsedText = `${roundedSeconds}秒`
        }
        progressState.value = {
          percent: percentValue,
          downloaded: clampedDone,
          total,
          seconds,
        }
        const downloadedText = formatBytes(clampedDone)
        const totalText = formatBytes(total)
        const writtenText = Number.isFinite(writtenBytes) && writtenBytes > 0
          ? `\n書き込み済み: ${formatBytes(Math.min(writtenBytes, total))}`
          : ''
        const progressText = `ダウンロード中: ${percentText}% (${downloadedText} / ${totalText})\n経過時間: ${elapsedText}${writtenText}`
        progressLabel.value = progressText
        setStatus(progressText)
      },
    })
    downloader.value = instance
    await recordDownloadLog()
    await instance.start()
    setStatus('ダウンロードが完了しました')
  } catch (e) {
    if (e instanceof DownloadCancelled || e?.name === 'AbortError') {
      setStatus('ダウンロードを中断しました')
    } else if (isFsAccess && isRangeUnsupportedError(e)) {
      const fallbackTarget = props.fallbackUrl || resolvedUrl
      if (fallbackTarget) {
        setStatus('高速ダウンロードを利用できなかったため通常ダウンロードに切り替えます')
        triggerBrowserDownload(fallbackTarget, filename || 'download')
        return
      }
      setStatus('高速ダウンロードを利用できませんでした')
    } else {
      const message = e?.message || e
      setStatus('エラー:', message)
    }
  } finally {
    busy.value = false
    downloader.value = null
  }
}

onBeforeUnmount(() => {
  if (manualStartTarget) {
    manualStartTarget.removeEventListener('fast-dl:start', handleManualStart)
    manualStartTarget = null
  }
  if (downloader.value) {
    downloader.value.cancel()
  }
})
</script>

<style scoped>
.fast-download { display: flex; flex-direction: column; gap: 8px; width: 100%; }
.fast-dl-start:disabled { opacity: 0.65; }
.fast-download-progress { display: flex; flex-direction: column; gap: 6px; width: 100%; }
.fast-download-progress-bar { width: 100%; height: 8px; }
.fast-download-progress-info { margin: 0; font-size: 13px; color: var(--text-main, #333); }
.status { margin: 0; font-size: 14px; white-space: pre-line; }
</style>
